import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const UserModal = ({ user, isOpen, onClose, onSave, mode = 'view' }) => {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    subscription: user?.subscription || 'free',
    status: user?.status || 'active',
    role: user?.role || 'user',
    notifications: user?.notifications || {
      email: true,
      sms: false,
      push: true
    }
  });

  const [activeTab, setActiveTab] = useState('profile');

  if (!isOpen) return null;

  const subscriptionOptions = [
    { value: 'free', label: 'Free' },
    { value: 'basic', label: 'Basic' },
    { value: 'pro', label: 'Pro' },
    { value: 'enterprise', label: 'Enterprise' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'pending', label: 'Pending' },
    { value: 'inactive', label: 'Inactive' }
  ];

  const roleOptions = [
    { value: 'user', label: 'User' },
    { value: 'admin', label: 'Admin' },
    { value: 'superadmin', label: 'Super Admin' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNotificationChange = (type, checked) => {
    setFormData(prev => ({
      ...prev,
      notifications: {
        ...prev?.notifications,
        [type]: checked
      }
    }));
  };

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: 'User' },
    { id: 'subscription', label: 'Subscription', icon: 'CreditCard' },
    { id: 'activity', label: 'Activity', icon: 'Activity' },
    { id: 'support', label: 'Support', icon: 'HelpCircle' }
  ];

  const mockActivityData = [
    { date: '2024-10-22', action: 'Login', details: 'Successful login from Chrome on Windows' },
    { date: '2024-10-21', action: 'Project Created', details: 'Created new project "Summer Campaign"' },
    { date: '2024-10-20', action: 'Video Rendered', details: 'Rendered 3 videos successfully' },
    { date: '2024-10-19', action: 'Subscription Updated', details: 'Upgraded to Pro plan' }
  ];

  const mockSupportTickets = [
    { id: '#12345', subject: 'Video rendering issue', status: 'Open', date: '2024-10-20' },
    { id: '#12344', subject: 'Billing question', status: 'Resolved', date: '2024-10-18' },
    { id: '#12343', subject: 'Feature request', status: 'In Progress', date: '2024-10-15' }
  ];

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-modal flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevation-3 w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center overflow-hidden">
              {user?.avatar ? (
                <img src={user?.avatar} alt={user?.avatarAlt} className="w-12 h-12 object-cover" />
              ) : (
                <Icon name="User" size={24} color="white" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {mode === 'edit' ? 'Edit User' : 'User Details'}
              </h2>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
          />
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <nav className="flex space-x-8 px-6">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 py-4 text-sm font-medium border-b-2 transition-micro ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Full Name"
                  value={formData?.name}
                  onChange={(e) => handleInputChange('name', e?.target?.value)}
                  disabled={mode === 'view'}
                />
                <Input
                  label="Email Address"
                  type="email"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  disabled={mode === 'view'}
                />
                <Select
                  label="Status"
                  options={statusOptions}
                  value={formData?.status}
                  onChange={(value) => handleInputChange('status', value)}
                  disabled={mode === 'view'}
                />
                <Select
                  label="Role"
                  options={roleOptions}
                  value={formData?.role}
                  onChange={(value) => handleInputChange('role', value)}
                  disabled={mode === 'view'}
                />
              </div>

              <div className="space-y-4">
                <h4 className="text-sm font-medium text-foreground">Notification Preferences</h4>
                <div className="space-y-3">
                  <Checkbox
                    label="Email Notifications"
                    checked={formData?.notifications?.email}
                    onChange={(e) => handleNotificationChange('email', e?.target?.checked)}
                    disabled={mode === 'view'}
                  />
                  <Checkbox
                    label="SMS Notifications"
                    checked={formData?.notifications?.sms}
                    onChange={(e) => handleNotificationChange('sms', e?.target?.checked)}
                    disabled={mode === 'view'}
                  />
                  <Checkbox
                    label="Push Notifications"
                    checked={formData?.notifications?.push}
                    onChange={(e) => handleNotificationChange('push', e?.target?.checked)}
                    disabled={mode === 'view'}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'subscription' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Subscription Plan"
                  options={subscriptionOptions}
                  value={formData?.subscription}
                  onChange={(value) => handleInputChange('subscription', value)}
                  disabled={mode === 'view'}
                />
                <div>
                  <label className="text-sm font-medium text-foreground">Next Billing Date</label>
                  <p className="text-sm text-muted-foreground mt-1">November 22, 2024</p>
                </div>
              </div>

              <div className="bg-muted rounded-lg p-4">
                <h4 className="text-sm font-medium text-foreground mb-2">Usage Statistics</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Videos Rendered:</span>
                    <span className="ml-2 font-medium text-foreground">47 / 100</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Storage Used:</span>
                    <span className="ml-2 font-medium text-foreground">2.3 GB / 10 GB</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Projects:</span>
                    <span className="ml-2 font-medium text-foreground">12 / 25</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Team Members:</span>
                    <span className="ml-2 font-medium text-foreground">3 / 5</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-foreground">Recent Activity</h4>
              <div className="space-y-3">
                {mockActivityData?.map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3 p-3 bg-muted rounded-lg">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-foreground">{activity?.action}</span>
                        <span className="text-xs text-muted-foreground">{activity?.date}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{activity?.details}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'support' && (
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-foreground">Support Tickets</h4>
              <div className="space-y-3">
                {mockSupportTickets?.map((ticket) => (
                  <div key={ticket?.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-foreground">{ticket?.id}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          ticket?.status === 'Open' ? 'bg-red-100 text-red-800' :
                          ticket?.status === 'In Progress'? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {ticket?.status}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{ticket?.subject}</p>
                      <p className="text-xs text-muted-foreground">{ticket?.date}</p>
                    </div>
                    <Button variant="ghost" size="sm" iconName="ExternalLink">
                      View
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {mode === 'edit' && (
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Save Changes
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserModal;